import { useEffect, useState } from 'react';
import { upperMantendoCursor } from '../../components/inputs/UpperInput';
import { Search, Eye, Ban } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import { Modal } from '../../components/Modal';
import { formatMoeda, formatDataHoraSP } from '@fiodecobre/shared';
import { ExtratoCredito, type MovimentacaoExtrato } from '../../components/trocas/ExtratoCredito';

interface CreditoRow {
  id: number;
  numero: string;
  cliente_id: number;
  cliente_nome: string;
  valor_original: string;
  valor_utilizado: string;
  saldo: string;
  origem_tipo: string;
  origem_id: number;
  origem_numero: string | null;
  status: 'ativo' | 'consumido' | 'cancelado';
  criado_em: string;
  atualizado_em: string;
}

// De onde o credito veio, em portugues. O credito do Programa do Prestador (DA-435/436) nao tem
// `origem_numero` — o LEFT JOIN da rota so alcanca `trocas` —, entao sem isto a coluna mostrava o
// literal do banco ("programa") numa tela em que o Ruan confere saldo de prestador.
function nomeOrigem(c: { origem_tipo: string; origem_numero: string | null }): string {
  if (c.origem_numero) return c.origem_numero;
  if (c.origem_tipo === 'programa') return 'Parceiro FIODECOBRE';
  if (c.origem_tipo === 'avulso') return 'Lancamento avulso';
  return c.origem_tipo;
}

// Movimentacao local extende MovimentacaoExtrato com campos extras do endpoint /api/creditos/:id
interface Movimentacao extends MovimentacaoExtrato {
  id: number;
  venda_id: number | null;
  motivo: string | null;
}

interface CreditoDetalhe extends CreditoRow {
  observacoes: string | null;
  operador_nome: string | null;
  movimentacoes: Movimentacao[];
}

export function CreditosLista() {
  const { usuario } = useAuth();
  const toast = useToast();
  const ehAdmin = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';

  const [status, setStatus] = useState<'todos' | 'ativo' | 'consumido' | 'cancelado'>('ativo');
  const [busca, setBusca] = useState('');
  const [lista, setLista] = useState<CreditoRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [detalhe, setDetalhe] = useState<CreditoDetalhe | null>(null);
  const [cancelar, setCancelar] = useState<CreditoRow | null>(null);
  const [motivoCancel, setMotivoCancel] = useState('');
  const [acaoAndando, setAcaoAndando] = useState(false);

  async function carregar() {
    setLoading(true);
    try {
      const r = await api.get<CreditoRow[]>('/api/creditos', { query: { status, limit: 300 } });
      setLista(r);
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void carregar(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const buscaLower = busca.trim().toLowerCase();
  const filtrada = lista.filter((c) => {
    if (!buscaLower) return true;
    return c.cliente_nome.toLowerCase().includes(buscaLower) ||
           c.numero.toLowerCase().includes(buscaLower) ||
           (c.origem_numero || '').toLowerCase().includes(buscaLower);
  });

  // Agrega saldo total por cliente (so creditos ativos)
  const saldoPorCliente = lista
    .filter((c) => c.status === 'ativo')
    .reduce<Record<string, { cliente_id: number; cliente_nome: string; saldo: number; qtd: number }>>((acc, c) => {
      const key = String(c.cliente_id);
      if (!acc[key]) acc[key] = { cliente_id: c.cliente_id, cliente_nome: c.cliente_nome, saldo: 0, qtd: 0 };
      acc[key].saldo += Number(c.saldo);
      acc[key].qtd += 1;
      return acc;
    }, {});
  const clientesComSaldo = Object.values(saldoPorCliente).sort((a, b) => b.saldo - a.saldo);

  async function abrirDetalhe(id: number) {
    try {
      const r = await api.get<CreditoDetalhe>(`/api/creditos/${id}`);
      setDetalhe(r);
    } catch (e: any) {
      toast.erro(e.message);
    }
  }

  async function confirmarCancelar() {
    if (!cancelar) return;
    setAcaoAndando(true);
    try {
      await api.post(`/api/creditos/${cancelar.id}/cancelar`, { motivo: motivoCancel.trim() || undefined });
      toast.sucesso(`Credito ${cancelar.numero} cancelado`);
      setCancelar(null);
      setMotivoCancel('');
      void carregar();
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setAcaoAndando(false);
    }
  }

  function statusBadge(c: CreditoRow) {
    if (c.status === 'ativo') return <span className="badge-verde">Ativo</span>;
    if (c.status === 'consumido') return <span style={{ fontSize: 11, padding: '1px 6px', background: '#dbeafe', color: '#1e40af', borderRadius: 4 }}>Consumido</span>;
    return <span className="badge-vermelho">Cancelado</span>;
  }

  return (
    <div>
      {/* Saldo por cliente (so na aba "ativo") */}
      {status === 'ativo' && clientesComSaldo.length > 0 && (
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: 14 }}>Saldos ativos por cliente</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {clientesComSaldo.map((c) => (
              <div
                key={c.cliente_id}
                style={{
                  padding: '6px 10px', background: '#fde68a', color: '#000',
                  borderRadius: 6, fontSize: 12, cursor: 'pointer',
                }}
                onClick={() => setBusca(c.cliente_nome)}
                title={`${c.qtd} credito(s)`}
              >
                <strong>{c.cliente_nome}</strong> · {formatMoeda(c.saldo)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filtros */}
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <div>
          <label className="form-label" style={{ display: 'block', marginBottom: 4, fontSize: 11 }}>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value as any)}>
            <option value="ativo">Ativos</option>
            <option value="consumido">Consumidos</option>
            <option value="cancelado">Cancelados</option>
            <option value="todos">Todos</option>
          </select>
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <label className="form-label" style={{ display: 'block', marginBottom: 4, fontSize: 11 }}>Busca</label>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              type="search"
              placeholder="Cliente, numero CRD-... ou origem TRC-..."
              value={busca}
              onChange={(e) => setBusca(upperMantendoCursor(e))}
              style={{ width: '100%', padding: '6px 12px 6px 32px', border: '1px solid var(--input-border)', borderRadius: 6, background: 'var(--input-bg)', color: 'var(--input-fg)' }}
            />
          </div>
        </div>
        <div style={{ alignSelf: 'flex-end', fontSize: 13, color: 'var(--text-muted)' }}>
          {filtrada.length} {filtrada.length === 1 ? 'credito' : 'creditos'}
        </div>
      </div>

      {/* Tabela */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</div>
      ) : filtrada.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Nenhum credito encontrado</div>
      ) : (
        <table className="tabela" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: 110 }}>Numero</th>
              <th>Cliente</th>
              <th style={{ width: 110 }}>Origem</th>
              <th style={{ width: 140 }}>Data</th>
              <th style={{ textAlign: 'right', width: 100 }}>Original</th>
              <th style={{ textAlign: 'right', width: 100 }}>Utilizado</th>
              <th style={{ textAlign: 'right', width: 100 }}>Saldo</th>
              <th style={{ width: 100 }}>Status</th>
              <th style={{ width: 100 }}>Acoes</th>
            </tr>
          </thead>
          <tbody>
            {filtrada.map((c) => (
              <tr key={c.id}>
                <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{c.numero}</td>
                <td>{c.cliente_nome}</td>
                <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{nomeOrigem(c)}</td>
                <td style={{ fontSize: 12 }}>{formatDataHoraSP(c.criado_em)}</td>
                <td style={{ textAlign: 'right' }}>{formatMoeda(c.valor_original)}</td>
                <td style={{ textAlign: 'right' }}>{formatMoeda(c.valor_utilizado)}</td>
                <td style={{ textAlign: 'right', fontWeight: 600, background: Number(c.saldo) > 0 ? '#fde68a' : undefined, color: Number(c.saldo) > 0 ? '#000' : undefined }}>
                  {formatMoeda(c.saldo)}
                </td>
                <td>{statusBadge(c)}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button type="button" className="btn-icon" onClick={() => abrirDetalhe(c.id)} title="Ver detalhe + movimentacoes">
                      <Eye size={14} />
                    </button>
                    {ehAdmin && c.status === 'ativo' && Number(c.valor_utilizado) === 0 && (
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={() => { setCancelar(c); setMotivoCancel(''); }}
                        title="Cancelar credito"
                        style={{ color: '#dc2626' }}
                      >
                        <Ban size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Modal detalhe */}
      <Modal aberto={!!detalhe} onFechar={() => setDetalhe(null)} titulo={detalhe ? `Credito ${detalhe.numero}` : ''} largura={760}>
        {detalhe && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, fontSize: 13 }}>
              <div><strong>Cliente:</strong> {detalhe.cliente_nome}</div>
              <div><strong>Status:</strong> {statusBadge(detalhe)}</div>
              <div><strong>Origem:</strong> {detalhe.origem_numero || `${nomeOrigem(detalhe)} #${detalhe.origem_id}`}</div>
              <div><strong>Criado:</strong> {formatDataHoraSP(detalhe.criado_em)} por {detalhe.operador_nome || '-'}</div>
            </div>
            {detalhe.observacoes && (
              <div style={{ fontSize: 13 }}><strong>Observacoes:</strong> {detalhe.observacoes}</div>
            )}

            <ExtratoCredito credito={{
              numero: detalhe.numero,
              valor_original: detalhe.valor_original,
              valor_utilizado: detalhe.valor_utilizado,
              saldo: detalhe.saldo,
              status: detalhe.status,
              criado_em: detalhe.criado_em,
              movimentacoes: detalhe.movimentacoes,
            }} />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn-secundario" onClick={() => setDetalhe(null)}>Fechar</button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal cancelar */}
      <Modal
        aberto={!!cancelar}
        onFechar={() => { setCancelar(null); setMotivoCancel(''); }}
        titulo={cancelar ? `Cancelar credito ${cancelar.numero}` : ''}
        largura={500}
      >
        {cancelar && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 13 }}>
              <div><strong>Cliente:</strong> {cancelar.cliente_nome}</div>
              <div><strong>Saldo:</strong> {formatMoeda(cancelar.saldo)}</div>
            </div>
            <div>
              <label className="form-label">Motivo (opcional)</label>
              <textarea
                value={motivoCancel}
                onChange={(e) => setMotivoCancel(e.target.value)}
                rows={3}
                style={{ width: '100%' }}
                autoFocus
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn-secundario" onClick={() => { setCancelar(null); setMotivoCancel(''); }} disabled={acaoAndando}>Voltar</button>
              <button type="button" className="btn-primario" onClick={confirmarCancelar} disabled={acaoAndando}>
                {acaoAndando ? 'Cancelando...' : 'Confirmar Cancelamento'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
